import { useState, useEffect, useCallback } from 'react';

export interface OnlineStatus {
  /** Whether the browser reports being online */
  isOnline: boolean;
  /** Whether we've detected an actual connection issue */
  hasConnectionIssue: boolean;
  /** Timestamp of when connection was lost */
  offlineSince: Date | null;
  /** Check actual connectivity by pinging an endpoint */
  checkConnection: () => Promise<boolean>;
}

/**
 * Hook to track online/offline status with real connectivity checks
 *
 * Uses navigator.onLine for instant feedback, but can also ping
 * an endpoint to verify actual connectivity (navigator.onLine can be
 * unreliable on some networks).
 *
 * Usage:
 * ```tsx
 * const { isOnline, hasConnectionIssue, checkConnection } = useOnlineStatus();
 *
 * if (!isOnline || hasConnectionIssue) {
 *   return <OfflineBanner />;
 * }
 * ```
 */
export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);
  const [offlineSince, setOfflineSince] = useState<Date | null>(null);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setOfflineSince(null);
      setHasConnectionIssue(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setOfflineSince(new Date());
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      setIsOnline(false);
      setOfflineSince(new Date());
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Ping an endpoint to verify actual connectivity
   * navigator.onLine can report true even when there's no real connection
   */
  const checkConnection = useCallback(async (): Promise<boolean> => {
    // If browser says offline, trust it
    if (!navigator.onLine) {
      setHasConnectionIssue(true);
      return false;
    }

    try {
      // Try to fetch a small resource with no-cache
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('/favicon.svg', {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const connected = response.ok;
      setHasConnectionIssue(!connected);
      return connected;
    } catch {
      // Network error - definitely offline or connection issue
      setHasConnectionIssue(true);
      return false;
    }
  }, []);

  return {
    isOnline,
    hasConnectionIssue,
    offlineSince,
    checkConnection,
  };
}
